import PoolOutlinedIcon from "@mui/icons-material/PoolOutlined";
import RoomServiceOutlinedIcon from "@mui/icons-material/RoomServiceOutlined";
import OutdoorGrillOutlinedIcon from "@mui/icons-material/OutdoorGrillOutlined";
import TrainOutlinedIcon from "@mui/icons-material/TrainOutlined";
import LocalParkingOutlinedIcon from "@mui/icons-material/LocalParkingOutlined";
import FitnessCenterOutlinedIcon from "@mui/icons-material/FitnessCenterOutlined";
import GarageOutlinedIcon from "@mui/icons-material/GarageOutlined";
import WaterOutlinedIcon from "@mui/icons-material/WaterOutlined";
import ElevatorOutlinedIcon from "@mui/icons-material/ElevatorOutlined";
import YardOutlinedIcon from "@mui/icons-material/YardOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import ChildCareOutlinedIcon from "@mui/icons-material/ChildCareOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import DeckOutlinedIcon from "@mui/icons-material/DeckOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";

/** Used when GET /api/location/assetCreationData fails or returns empty sections. */
export const FALLBACK_ASSET_CREATION_DATA = {
  propertyTypeOptions: ["Residential", "Commercial", "Industrial", "Land", "Retail"],
  energyTypeOptions: {
    SOLAR: "Solar",
    WIND: "Wind",
    HYDRO: "Hydro",
    GEOTHERMAL: "Geothermal",
    BIOMASS: "Biomass",
  },
  commodityTypeOptions: {
    GOLD: "Gold",
    SILVER: "Silver",
    PLATINUM: "Platinum",
    PALLADIUM: "Palladium",
    CRUDE_OIL: "Crude Oil",
    OTHER: "Other",
  },
  spvStructureTypeOptions: {
    LIMITED_LIABILITY_COMPANY: "Limited Liability Company (LLC)",
    PRIVATE_LIMITED_COMPANY: "Private Limited Company(LTD)",
    SEGREGATED_PORTFOLIO_COMPANY: "Segregated Portfolio Company(SPC)",
    TRUST: "Trust",
    FOUNDATION: "Foundation",
  },
  energyUnitOptions: {
    MW: "MW",
    KW: "KW",
    GW: "GW",
  },
  artConditionOptions: {
    MINT: "Mint",
    EXCELLENT: "Excellent",
    GOOD: "Good",
    FAIR: "Fair",
  },
  luxuryConditionOptions: {
    MINT: "Mint/Unworn",
    EXCELLENT: "Excellent",
    GOOD: "Good",
    FAIR: "Fair",
  },
  inventoryTypeOptions: {
    RAW_MATERIAL: "Raw Material",
    FINISHED_GOODS: "Finished Goods",
    WORK_IN_PROGRESS: "Work In Progress",
    TRADING_GOODS: "Trading Goods",
  },
  creditRatingOptions: {
    AAA: "AAA",
    AA: "AA",
    A: "A",
    BBB: "BBB",
    BB: "BB",
    UNRATED: "Unrated",
  },
  islamicFinanceProductTypeOptions: {
    SUKUK: "Sukuk (Islamic Bond)",
    MURABAHA: "Murabaha (Cost-Plus)",
    IJARA: "Ijara (Lease)",
    MUSHARAKA: "Musharaka (Partnership)",
    MUDARABA: "Mudaraba (Profit-Sharing)",
  },
  sizeUnitOptions: {
    SQ_FT: "sq ft",
    SQ_M: "sq m",
    ACRES: "acres",
  },
  featuresAndAmenitiesOptions: [
    "Swimming Pool",
    "Gym & Spa",
    "24/7 Security",
    "Concierge Service",
    "Underground Parking",
    "Kids Play Area",
    "BBQ Area",
    "Marina Views",
    "Beach Access",
    "Metro Access",
    "High-Speed Elevators",
    "Business Lounge",
    "Covered Parking",
    "Garden",
    "Rooftop Terrace",
  ],
};

const AMENITY_ICON_RULES = [
  { test: (s) => s.includes("swimming") || s.includes("pool"), Icon: PoolOutlinedIcon },
  { test: (s) => s.includes("concierge"), Icon: RoomServiceOutlinedIcon },
  { test: (s) => s.includes("bbq"), Icon: OutdoorGrillOutlinedIcon },
  { test: (s) => s.includes("metro"), Icon: TrainOutlinedIcon },
  { test: (s) => s.includes("covered") && s.includes("park"), Icon: LocalParkingOutlinedIcon },
  { test: (s) => s.includes("gym") || s.includes("spa"), Icon: FitnessCenterOutlinedIcon },
  { test: (s) => s.includes("underground") && s.includes("park"), Icon: GarageOutlinedIcon },
  { test: (s) => s.includes("marina"), Icon: WaterOutlinedIcon },
  { test: (s) => s.includes("elevator"), Icon: ElevatorOutlinedIcon },
  { test: (s) => s.includes("garden"), Icon: YardOutlinedIcon },
  { test: (s) => s.includes("security"), Icon: SecurityOutlinedIcon },
  { test: (s) => s.includes("kid"), Icon: ChildCareOutlinedIcon },
  { test: (s) => s.includes("beach"), Icon: BeachAccessOutlinedIcon },
  { test: (s) => s.includes("lounge") || s.includes("business"), Icon: BusinessCenterOutlinedIcon },
  { test: (s) => s.includes("rooftop") || s.includes("terrace"), Icon: DeckOutlinedIcon },
];

export function iconForAmenityLabel(label) {
  const s = String(label || "").toLowerCase();
  for (const { test, Icon } of AMENITY_ICON_RULES) {
    if (test(s)) return Icon;
  }
  return StarBorderOutlinedIcon;
}

function pickArray(src, key, fallback) {
  const v = src[key];
  return Array.isArray(v) && v.length ? v : fallback;
}

function pickRecord(src, key, fallback) {
  const v = src[key];
  return v && typeof v === "object" && !Array.isArray(v) && Object.keys(v).length ? v : fallback;
}

/** @param apiResponse Full API JSON e.g. `{ success, data }` from useAssetCreationDataQuery */
export function mergeAssetCreationData(apiResponse) {
  const inner = apiResponse?.data && typeof apiResponse.data === "object" ? apiResponse.data : {};
  const fb = FALLBACK_ASSET_CREATION_DATA;
  return {
    propertyTypeOptions: pickArray(inner, "propertyTypeOptions", fb.propertyTypeOptions),
    energyTypeOptions: pickRecord(inner, "energyTypeOptions", fb.energyTypeOptions),
    commodityTypeOptions: pickRecord(inner, "commodityTypeOptions", fb.commodityTypeOptions),
    spvStructureTypeOptions: pickRecord(inner, "spvStructureTypeOptions", fb.spvStructureTypeOptions),
    energyUnitOptions: pickRecord(inner, "energyUnitOptions", fb.energyUnitOptions),
    artConditionOptions: pickRecord(inner, "artConditionOptions", fb.artConditionOptions),
    luxuryConditionOptions: pickRecord(inner, "luxuryConditionOptions", fb.luxuryConditionOptions),
    inventoryTypeOptions: pickRecord(inner, "inventoryTypeOptions", fb.inventoryTypeOptions),
    creditRatingOptions: pickRecord(inner, "creditRatingOptions", fb.creditRatingOptions),
    islamicFinanceProductTypeOptions: pickRecord(
      inner,
      "islamicFinanceProductTypeOptions",
      fb.islamicFinanceProductTypeOptions
    ),
    sizeUnitOptions: pickRecord(inner, "sizeUnitOptions", fb.sizeUnitOptions),
    featuresAndAmenitiesOptions: pickArray(
      inner,
      "featuresAndAmenitiesOptions",
      fb.featuresAndAmenitiesOptions
    ),
  };
}

const SIZE_UNIT_ORDER = ["SQ_FT", "SQ_M", "ACRES", "HECTARES", "SQ_KM"];

export function orderedSizeUnitEntries(sizeUnitOptions) {
  const opts = sizeUnitOptions && typeof sizeUnitOptions === "object" ? sizeUnitOptions : {};
  const keys = Object.keys(opts);
  keys.sort(
    (a, b) =>
      (SIZE_UNIT_ORDER.indexOf(a) === -1 ? 999 : SIZE_UNIT_ORDER.indexOf(a)) -
        (SIZE_UNIT_ORDER.indexOf(b) === -1 ? 999 : SIZE_UNIT_ORDER.indexOf(b)) || a.localeCompare(b)
  );
  return keys.map((key) => ({ key, label: opts[key] }));
}
